import { HTTP } from "@/api";
import { isAxiosError } from "axios";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  type DefaultPaginatedFilters,
  type PaginatedResponse,
  filtersToQueryString,
} from "./filters";

export const API_V1 = "api/v1";

/**
 * Ensures the path is prefixed with `api/v1/`.
 * - Strips any leading slashes.
 * - If the path already starts with `api/v1/`, it is returned as-is (idempotent).
 * - `auth/` paths are left unchanged (authentication endpoints do not use api/v1).
 * - Otherwise, `api/v1/` is prepended.
 */
const withV1 = (path: string): string => {
  const stripped = path.replace(/^\/+/, "");
  if (stripped.startsWith(`${API_V1}/`) || stripped.startsWith("auth/")) return stripped;
  return `${API_V1}/${stripped}`;
};

export interface CrudOptions {
  trailingSlash?: boolean;
  transform?: (payload: any) => any;
  responseType?: AxiosRequestConfig["responseType"];
  headers?: AxiosRequestConfig["headers"];
  timeout?: AxiosRequestConfig["timeout"];
}

/**
 * Base constraint for a path-parameter object.
 */
export type PathParamsBase = Record<string, string | number>;

/**
 * The path-params type for a route with no `{placeholder}` segments.
 * This is the default for every factory's `TPathParams`, so endpoints without
 * placeholders don't need to declare a type at all.
 *
 * Deliberately an empty object rather than `Record<string, never>`: an index
 * signature over `never` would intersect with the implicit `{id}` added by
 * `WithItemId` and collapse it to `never`, rejecting every `get({ id })` call.
 */
export type NoPathParams = Record<never, never>;

/**
 * Adds the implicit `{id}` placeholder that item-level routes always carry, so
 * services with a flat `slug/{id}` route don't need to declare a params type.
 */
export type WithItemId<TPathParams> = TPathParams & { id?: string | number };

/**
 * Makes the leading `params` argument optional when `P` has no *required* keys
 * (i.e. the route has no placeholders, or all of them are optional).
 *
 * Note that `params` can only be omitted when nothing follows it — filters and
 * queries occupy later positions, so pass `{}` (or `undefined`) when you need them.
 */
type ParamArgs<P> = NoPathParams extends P ? [params?: P] : [params: P];

const hasSearch = (f?: Partial<DefaultPaginatedFilters>) =>
  Boolean((f?.query && String(f.query).trim()) || (f?.search && String(f.search).trim()));

const join = (...parts: Array<string | undefined>) =>
  parts
    .filter(Boolean)
    .map((p) => String(p).replace(/^\/+|\/+$/g, ""))
    .join("/");

const withTrailingSlash = (p: string, apply: boolean = true) =>
  apply ? (p.endsWith("/") ? p : `${p}/`) : p;

const buildUrl = (path: string, qs?: string) => (qs ? `${path}?${qs}` : path);

/**
 * Removes keys from a query object that are already present in a params object.
 */
const omiTPathParams = <T extends Record<string, any>>(
  queries: T | undefined,
  params: Record<string, any>
): Partial<T> => {
  if (!queries) return {};
  const clean = { ...queries };
  Object.keys(params).forEach((key) => {
    delete clean[key as keyof T];
  });
  return clean;
};

/**
 * Replaces placeholders like {id} with actual values from a params object.
 */
export function fillUrlTemplate(template: string, params: Record<string, string | number>): string {
  let url = template;
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    // Matches {key}
    const regex = new RegExp(`({)${key}(})?`, "g");
    url = url.replace(regex, String(value));
  }
  return url;
}

/**
 * Creates a generic GET request for multiple resources from a template route.
 * @template T - The response item type (becomes array T[]).
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @template TFilters - Paginated filters for the query string.
 * @param template A route string containing parameters e.g., `user/{userId}/posts`
 * @param searchTemplate Optional route string used instead if search filters are provided.
 * @returns An async function `(params: TPathParams, filters?: Partial<TFilters>) => Promise<AxiosResponse<T[]>>`
 */
export const getAllPathParam =
  <
    T,
    TPathParams extends PathParamsBase = NoPathParams,
    TFilters extends DefaultPaginatedFilters = DefaultPaginatedFilters,
  >(
    template: string,
    searchTemplate?: string,
    opts?: CrudOptions
  ) =>
  async (
    ...args: [...ParamArgs<TPathParams>, filters?: Partial<TFilters>]
  ): Promise<AxiosResponse<T[]>> => {
    const [rawParams, filters] = args as unknown as [
      TPathParams | undefined,
      Partial<TFilters> | undefined,
    ];
    const params = rawParams ?? ({} as TPathParams);
    const baseTpl = hasSearch(filters) && searchTemplate ? searchTemplate : template;
    const path = withTrailingSlash(
      fillUrlTemplate(withV1(baseTpl), params),
      opts?.trailingSlash !== false
    );
    return HTTP.get<T[]>(path, { params: omiTPathParams(filters, params) });
  };

/**
 * Creates a generic GET request for a single resource from a template route.
 * @template T - The response item type.
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @param template A route string containing parameters e.g., `user/{userId}/posts/{id}`
 * @returns An async function `(params: TPathParams, queries?: AxiosRequestConfig["params"]) => Promise<AxiosResponse<T>>`
 */
export const getPathParam =
  <T, TPathParams extends PathParamsBase = NoPathParams>(
    template: string,
    defaultQueries?: AxiosRequestConfig["params"],
    opts?: CrudOptions
  ) =>
  async (
    ...args: [...ParamArgs<TPathParams>, queries?: AxiosRequestConfig["params"]]
  ): Promise<AxiosResponse<T>> => {
    const [rawParams, queries] = args as unknown as [
      TPathParams | undefined,
      AxiosRequestConfig["params"],
    ];
    const params = rawParams ?? ({} as TPathParams);
    const path = withTrailingSlash(
      fillUrlTemplate(withV1(template), params),
      opts?.trailingSlash !== false
    );
    try {
      return await HTTP.get<T>(path, {
        params: omiTPathParams({ ...defaultQueries, ...queries }, params),
        responseType: opts?.responseType,
        headers: opts?.headers,
        timeout: opts?.timeout,
      });
    } catch (err) {
      if (
        isAxiosError(err) &&
        err.response?.data instanceof Blob &&
        err.response.data.type.includes("json")
      ) {
        try {
          err.response.data = JSON.parse(await err.response.data.text());
        } catch {}
      }

      throw err;
    }
  };

/**
 * Creates a generic POST request for creating a resource from a template route.
 * @template T - The response item type.
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @template TCreate - The payload structure required for creation.
 * @param template A route string containing parameters e.g., `user/{userId}/posts`
 * @returns An async function `(params: TPathParams, payload: TCreate, queries?: AxiosRequestConfig["params"]) => Promise<AxiosResponse<T>>`
 */
export const createPathParam =
  <T, TPathParams extends PathParamsBase = NoPathParams, TCreate = Partial<T>>(
    template: string,
    opts?: CrudOptions
  ) =>
  async (
    ...args: [...ParamArgs<TPathParams>, payload: TCreate, queries?: AxiosRequestConfig["params"]]
  ): Promise<AxiosResponse<T>> => {
    const [rawParams, payload, queries] = args as unknown as [
      TPathParams | undefined,
      TCreate,
      AxiosRequestConfig["params"],
    ];
    const params = rawParams ?? ({} as TPathParams);
    const basePath = fillUrlTemplate(withV1(template), params);
    const path = withTrailingSlash(join(basePath), opts?.trailingSlash !== false);
    const finalPayload = opts?.transform ? opts.transform(payload) : payload;
    return HTTP.post<T>(path, finalPayload, {
      params: omiTPathParams(queries, params),
      headers: opts?.headers,
      timeout: opts?.timeout,
    });
  };

/**
 * Creates a generic PATCH request for updating a resource from a template route.
 * @template T - The response item type.
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @template TUpdate - The payload structure required for updating.
 * @param template A route string containing parameters e.g., `user/{userId}/posts/{id}`
 * @returns An async function `(params: TPathParams, payload: TUpdate, queries?: AxiosRequestConfig["params"]) => Promise<AxiosResponse<T>>`
 */
export const updatePathParam =
  <T, TPathParams extends PathParamsBase = NoPathParams, TUpdate = Partial<T>>(
    template: string,
    opts?: CrudOptions
  ) =>
  async (
    ...args: [...ParamArgs<TPathParams>, payload: TUpdate, queries?: AxiosRequestConfig["params"]]
  ): Promise<AxiosResponse<T>> => {
    const [rawParams, payload, queries] = args as unknown as [
      TPathParams | undefined,
      TUpdate,
      AxiosRequestConfig["params"],
    ];
    const params = rawParams ?? ({} as TPathParams);
    const path = withTrailingSlash(
      fillUrlTemplate(withV1(template), params),
      opts?.trailingSlash !== false
    );
    const finalPayload = opts?.transform ? opts.transform(payload) : payload;
    return HTTP.patch<T>(path, finalPayload, {
      params: omiTPathParams(queries, params),
      headers: opts?.headers,
      timeout: opts?.timeout,
    });
  };

/**
 * Creates a generic PUT request for replacing a resource from a template route.
 * Use it for endpoints that accept PUT but not PATCH; otherwise prefer `updatePathParam`.
 * @template T - The response item type.
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @template TReplace - The payload structure sent with the request.
 * @param template A route string containing parameters e.g., `user/{userId}/settings`
 * @returns An async function `(params: TPathParams, payload: TReplace, queries?: AxiosRequestConfig["params"]) => Promise<AxiosResponse<T>>`
 */
export const replacePathParam =
  <T, TPathParams extends PathParamsBase = NoPathParams, TReplace = Partial<T>>(
    template: string,
    opts?: CrudOptions
  ) =>
  async (
    ...args: [...ParamArgs<TPathParams>, payload: TReplace, queries?: AxiosRequestConfig["params"]]
  ): Promise<AxiosResponse<T>> => {
    const [rawParams, payload, queries] = args as unknown as [
      TPathParams | undefined,
      TReplace,
      AxiosRequestConfig["params"],
    ];
    const params = rawParams ?? ({} as TPathParams);
    const path = withTrailingSlash(
      fillUrlTemplate(withV1(template), params),
      opts?.trailingSlash !== false
    );
    const finalPayload = opts?.transform ? opts.transform(payload) : payload;
    return HTTP.put<T>(path, finalPayload, {
      params: omiTPathParams(queries, params),
      headers: opts?.headers,
      timeout: opts?.timeout,
    });
  };

/**
 * Creates a generic DELETE request for a single resource from a template route.
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @param template A route string containing parameters e.g., `user/{userId}/posts/{id}`
 * @returns An async function `(params: TPathParams, queries?: AxiosRequestConfig["params"]) => Promise<AxiosResponse<void>>`
 */
export const removePathParam =
  <TPathParams extends PathParamsBase = NoPathParams>(template: string, opts?: CrudOptions) =>
  async (
    ...args: [...ParamArgs<TPathParams>, queries?: AxiosRequestConfig["params"]]
  ): Promise<AxiosResponse<void>> => {
    const [rawParams, queries] = args as unknown as [
      TPathParams | undefined,
      AxiosRequestConfig["params"],
    ];
    const params = rawParams ?? ({} as TPathParams);
    const path = withTrailingSlash(
      fillUrlTemplate(withV1(template), params),
      opts?.trailingSlash !== false
    );
    return HTTP.delete<void>(path, {
      params: omiTPathParams(queries, params),
      headers: opts?.headers,
      timeout: opts?.timeout,
    });
  };

/**
 * Creates a paginated GET request for a resource collection from a template route.
 * @template T - The response item type (returns fully unpacked paginated wrapper).
 * @template TPathParams - Path parameters object, fields matching template placeholders format `:field`.
 * @template TFilters - Paginated filters for the query string.
 * @param template A route string containing parameters e.g., `user/{userId}/posts`
 * @param searchTemplate Optional route string used instead if search filters are provided.
 * @param fieldMap An optional record mapping frontend filter keys to backend query string keys.
 * @returns An async function `(params: TPathParams, filters: Partial<TFilters>, cursor?: string, extraPath?: string) => Promise<PaginatedResponse<T>>`
 */
export const getPaginatedFilteredPathParam =
  <
    T,
    TPathParams extends PathParamsBase = NoPathParams,
    TFilters extends DefaultPaginatedFilters = DefaultPaginatedFilters,
  >(
    template: string,
    searchTemplate?: string,
    fieldMap?: Record<string, string>,
    opts?: CrudOptions
  ) =>
  async (
    ...args: [
      ...ParamArgs<TPathParams>,
      filters: Partial<TFilters>,
      cursor?: string,
      extraPath?: string,
    ]
  ): Promise<PaginatedResponse<T>> => {
    const [rawParams, filters, cursor, rawExtraPath] = args as unknown as [
      TPathParams | undefined,
      Partial<TFilters>,
      string | undefined,
      string | undefined,
    ];
    const params = rawParams ?? ({} as TPathParams);
    const extraPath = rawExtraPath ?? "";
    const cleanFilters = omiTPathParams(filters, params);
    const qs = filtersToQueryString(cleanFilters, fieldMap);
    const baseTpl = hasSearch(cleanFilters) && searchTemplate ? searchTemplate : template;
    const basePath = fillUrlTemplate(withV1(baseTpl), params);
    const path = withTrailingSlash(join(basePath, extraPath), opts?.trailingSlash !== false);
    const url = cursor ?? buildUrl(path, qs);
    const { data } = await HTTP.get<PaginatedResponse<T>>(url);
    return data;
  };

/**
 * The name of every operation `createPathParamCRUD` can generate.
 */
export type CrudMethodName =
  | "getAll"
  | "get"
  | "create"
  | "update"
  | "remove"
  | "getPaginatedFiltered";

/**
 * Options accepted by the object-form of `createPathParamCRUD`, folding the
 * positional `searchTemplate`/`fieldMap`/`itemTemplate` arguments in alongside
 * `CrudOptions` and the `only`/`except` method selection.
 */
export interface CrudFactoryOptions extends CrudOptions {
  /** Full alternative template used when a `search`/`query` filter is present. */
  searchTemplate?: string;
  /** Maps frontend filter keys to backend query-string keys. */
  fieldMap?: Record<string, string>;
  /** Item route for `get`/`update`/`remove`. Defaults to `${template}/{id}`. */
  itemTemplate?: string;
  /**
   * Restricts the generated service to these operations. The omitted keys are
   * absent from the returned object at runtime.
   *
   * Call through `crudFor` to have the *type* narrowed to the listed keys too.
   */
  only?: readonly CrudMethodName[];
  /**
   * Removes these operations from the generated service. Applied after `only`.
   */
  except?: readonly CrudMethodName[];
}

type CrudMethods<
  T,
  TPathParams extends PathParamsBase,
  TFilters extends DefaultPaginatedFilters,
  TCreate,
  TUpdate,
> = {
  getAll: ReturnType<typeof getAllPathParam<T, TPathParams, TFilters>>;
  get: ReturnType<typeof getPathParam<T, WithItemId<TPathParams>>>;
  create: ReturnType<typeof createPathParam<T, TPathParams, TCreate>>;
  update: ReturnType<typeof updatePathParam<T, WithItemId<TPathParams>, TUpdate>>;
  remove: ReturnType<typeof removePathParam<WithItemId<TPathParams>>>;
  getPaginatedFiltered: ReturnType<typeof getPaginatedFilteredPathParam<T, TPathParams, TFilters>>;
};

/**
 * Curried entry point for services that expose only a subset of the CRUD
 * operations. The first call fixes the entity/filter/payload generics, the
 * second names the operations to keep:
 *
 * ```typescript
 * crudFor<TQRCode>()("qr", { only: ["getAll", "getPaginatedFiltered"] });
 * ```
 *
 * Currying is required because TypeScript falls back to a type parameter's
 * default as soon as *any* type argument is supplied explicitly — so a trailing
 * `TMethod` on `createPathParamCRUD` could never be inferred from `only`
 * whenever `T` was written out, which is every call site.
 */
export const crudFor =
  <
    T,
    TPathParams extends PathParamsBase = NoPathParams,
    TFilters extends DefaultPaginatedFilters = DefaultPaginatedFilters,
    TCreate = Partial<T>,
    TUpdate = Partial<T>,
  >() =>
  <
    const TOnly extends CrudMethodName = CrudMethodName,
    const TExcept extends CrudMethodName = never,
  >(
    template: string,
    options?: Omit<CrudFactoryOptions, "only" | "except"> & {
      only?: readonly TOnly[];
      except?: readonly TExcept[];
    }
  ): Pick<CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate>, Exclude<TOnly, TExcept>> =>
    createPathParamCRUD<T, TPathParams, TFilters, TCreate, TUpdate>(
      template,
      options as CrudFactoryOptions & { only?: undefined; except?: undefined }
    );

const CRUD_METHOD_NAMES: readonly CrudMethodName[] = [
  "getAll",
  "get",
  "create",
  "update",
  "remove",
  "getPaginatedFiltered",
];

/**
 * Generates an object containing the standard asynchronous CRUD operations automatically bound
 * to dynamic URL templates.
 *
 * This entry point always generates all six operations. To expose a subset, use `crudFor`,
 * which narrows the return type as well as the runtime object:
 *
 * ```typescript
 * crudFor<TQRCode>()(SLUG, { only: ["getAll", "getPaginatedFiltered"] })
 * ```
 *
 * @template T - The base response entity type.
 * @template TPathParams - Path parameters object required across all nested operations.
 *                     Make sure to include specific item fields like `id` if `get`, `update`, and `remove` require it.
 * @template TFilters - Paginated filters for list queries.
 * @template TCreate - The expected payload for `create()`.
 * @template TUpdate - The expected payload for `update()`.
 *
 * @param template The root list template route e.g., `message/room/{roomId}/messages`
 *                 This is used for `getAll`, `create`, and `getPaginatedFiltered`.
 * @param searchTemplate Optional alternative list route to use if `search` filters dictate it.
 *                 May also be an options object carrying `searchTemplate`, `fieldMap`,
 *                 `itemTemplate` and any `CrudOptions`.
 * @param fieldMap Optional mapping dictionary between query parameter keys.
 * @param itemTemplate The nested path for individual item requests. Defaults to joining `{id}`
 *                     e.g., `message/room/{roomId}/messages/{id}`
 *                     This is used for `get`, `update`, and `remove`.
 * @returns An object containing `getAll`, `get`, `create`, `update`, `remove`, and
 *          `getPaginatedFiltered` functions.
 */
export function createPathParamCRUD<
  T,
  TPathParams extends PathParamsBase = NoPathParams,
  TFilters extends DefaultPaginatedFilters = DefaultPaginatedFilters,
  TCreate = Partial<T>,
  TUpdate = Partial<T>,
>(
  template: string,
  options?: CrudFactoryOptions & { only?: undefined; except?: undefined }
): CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate>;
export function createPathParamCRUD<
  T,
  TPathParams extends PathParamsBase = NoPathParams,
  TFilters extends DefaultPaginatedFilters = DefaultPaginatedFilters,
  TCreate = Partial<T>,
  TUpdate = Partial<T>,
>(
  template: string,
  searchTemplate?: string,
  fieldMap?: Record<string, string>,
  itemTemplate?: string,
  opts?: CrudOptions
): CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate>;
export function createPathParamCRUD<
  T,
  TPathParams extends PathParamsBase = NoPathParams,
  TFilters extends DefaultPaginatedFilters = DefaultPaginatedFilters,
  TCreate = Partial<T>,
  TUpdate = Partial<T>,
>(
  template: string,
  searchTemplateOrOptions?: string | CrudFactoryOptions,
  fieldMapArg?: Record<string, string>,
  itemTemplateArg?: string,
  optsArg?: CrudOptions
): CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate> {
  const objectForm =
    typeof searchTemplateOrOptions === "object" && searchTemplateOrOptions !== null;
  const config: CrudFactoryOptions = objectForm
    ? searchTemplateOrOptions
    : {
        ...optsArg,
        searchTemplate: searchTemplateOrOptions,
        fieldMap: fieldMapArg,
        itemTemplate: itemTemplateArg,
      };

  const { searchTemplate, fieldMap, itemTemplate: rawItemTemplate, only, except, ...opts } = config;
  const itemTemplate = rawItemTemplate ?? join(template, "{id}");

  const selected = (only ?? CRUD_METHOD_NAMES).filter((name) => !except?.includes(name));

  const builders: {
    [K in CrudMethodName]: () => CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate>[K];
  } = {
    getAll: () => getAllPathParam<T, TPathParams, TFilters>(template, searchTemplate, opts),
    get: () => getPathParam<T, WithItemId<TPathParams>>(itemTemplate, undefined, opts),
    create: () => createPathParam<T, TPathParams, TCreate>(template, opts),
    update: () => updatePathParam<T, WithItemId<TPathParams>, TUpdate>(itemTemplate, opts),
    remove: () => removePathParam<WithItemId<TPathParams>>(itemTemplate, opts),
    getPaginatedFiltered: () =>
      getPaginatedFilteredPathParam<T, TPathParams, TFilters>(
        template,
        searchTemplate,
        fieldMap,
        opts
      ),
  };

  const service: Partial<CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate>> = {};
  for (const name of selected) {
    service[name] = builders[name]() as never;
  }
  return service as CrudMethods<T, TPathParams, TFilters, TCreate, TUpdate>;
}

/**
 * Creates a generic GET request for a customized nested action path under an item dynamically.
 * @template T - The response item type.
 * @template TPathParams - Path parameters object, required fields matching placeholders format `:field`.
 * @param template The parent collection route.
 * @param nestedAction The exact action path appended to the item route.
 * @param itemTemplate The individual item URL route used to build the prefix. Defaults to joining `{id}`.
 * @returns An async function `(params: TPathParams, queries?: AxiosRequestConfig["params"]) => Promise<AxiosResponse<T>>`
 */
export const createPathParamNestedGet =
  <T, TPathParams extends PathParamsBase = NoPathParams>(
    template: string,
    nestedAction: string,
    itemTemplate: string = join(template, "{id}"),
    opts?: CrudOptions
  ) =>
  async (
    ...args: [...ParamArgs<TPathParams>, queries?: AxiosRequestConfig["params"]]
  ): Promise<AxiosResponse<T>> => {
    const [rawParams, queries] = args as unknown as [
      TPathParams | undefined,
      AxiosRequestConfig["params"],
    ];
    const params = rawParams ?? ({} as TPathParams);
    const basePath = fillUrlTemplate(withV1(itemTemplate), params);
    const path = withTrailingSlash(join(basePath, nestedAction), opts?.trailingSlash !== false);
    return HTTP.get<T>(path, { params: omiTPathParams(queries, params) });
  };
